import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, CircularProgress, useTheme, alpha, Button, Snackbar, Alert,
} from '@mui/material';
import { LocalShipping as SupplierIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import venueSupplierService from '../../../../services/venueSupplierService';
import { useAuth } from '../../../../context/AuthContext';
import ConnectSupplierDialog from './ConnectSupplierDialog';
import type { Supplier, SupplierType } from '../../../../types/venue';

const SUPPLIER_TYPES: SupplierType[] = ['DISTRIBUTOR', 'DIRECT_IMPORT', 'AUCTION', 'MERCHANT', 'OTHER'];

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

/**
 * Venue-scoped supplier list. Backend filters to suppliers connected to this
 * venue — via SupplierItemLink, PurchaseOrder, Invoice, or an explicit grant —
 * so VENUE_USER never sees a global directory.
 *
 * SUPPLIER-CONNECT.1 — a system admin can connect one from here. Before this,
 * the only way to let a venue use an existing supplier was to post a receipt or
 * raise a PO on their behalf and let the side effect grant the access, which
 * meant a permission could not be given without also committing to an order.
 */
const VenueSupplierList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();

  const { isSystemAdmin } = useAuth();
  const canConnect = isSystemAdmin();

  const [connectOpen, setConnectOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [items, setItems] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });

  const fetchItems = useCallback(
    async (vId: string, query: string, type: string, page: number, pageSize: number) => {
      setLoading(true);
      try {
        const result = await venueSupplierService.listVenueSuppliers(
          vId,
          page + 1,
          pageSize,
          query || undefined,
          type || undefined,
        );
        setItems(result.data);
        setPagination(prev => ({
          ...prev,
          page,
          pageSize,
          totalItems: result.pagination.totalItems,
          totalPages: result.pagination.totalPages,
        }));
      } catch (err) {
        console.error('Failed to load venue suppliers', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const debouncedFetch = useMemo(
    () =>
      debounce(
        (vId: string, query: string, type: string, page: number, pageSize: number) => {
          fetchItems(vId, query, type, page, pageSize);
        },
        300,
      ),
    [fetchItems],
  );

  useEffect(() => {
    if (!venueId) return;
    debouncedFetch(venueId, searchQuery, typeFilter, pagination.page, pagination.pageSize);
    return () => debouncedFetch.cancel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, searchQuery, typeFilter, pagination.page, pagination.pageSize]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SupplierIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Suppliers</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Suppliers connected to this venue
            </Typography>
            {canConnect && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setConnectOpen(true)}
              >
                Connect supplier
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <TextField
            size="small"
            placeholder="Search suppliers…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPagination(prev => ({ ...prev, page: 0 })); }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={e => { setTypeFilter(e.target.value); setPagination(prev => ({ ...prev, page: 0 })); }}
            >
              <MenuItem value="">All types</MenuItem>
              {SUPPLIER_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Type</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Country</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Account Ref</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Contact</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <SupplierIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">
                        No suppliers are connected to this venue yet.
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Suppliers appear here once you link them to inventory items, raise a purchase order, or record an invoice.
                      </Typography>
                      {/* The dead end this fixes: a venue user cannot see the
                          global catalogue, so "search harder" is not advice.
                          Name who can grant it. */}
                      {canConnect ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          sx={{ mt: 2 }}
                          onClick={() => setConnectOpen(true)}
                        >
                          Connect supplier
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1.5 }}>
                          If you need to buy from a supplier that is not listed,
                          ask a system administrator to connect them to this venue.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(supplier => (
                  <TableRow
                    key={supplier._id}
                    hover
                    onClick={() => navigate(`/admin/venues/${venueId}/suppliers/${supplier._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{supplier.name}</Typography>
                      {supplier.shortName && (
                        <Typography variant="caption" color="text.secondary">{supplier.shortName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {supplier.type.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{supplier.country || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {supplier.accountReference || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{supplier.contactName || '—'}</Typography>
                      {supplier.contactEmail && (
                        <Typography variant="caption" color="text.secondary">{supplier.contactEmail}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={supplier.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={supplier.active ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {canConnect && (
          <ConnectSupplierDialog
            open={connectOpen}
            venueId={venueId ?? ''}
            connectedIds={new Set(items.map(i => i._id))}
            onClose={() => setConnectOpen(false)}
            onConnected={(supplier) => {
              setToast(`${supplier.name} connected to this venue.`);
              // Refetch rather than splice it in: the list is paginated and
              // sorted server-side, so a local insert would show it in the
              // wrong place until the next load.
              if (venueId) {
                fetchItems(venueId, searchQuery, typeFilter, pagination.page, pagination.pageSize);
              }
            }}
          />
        )}

        <Snackbar
          open={!!toast}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setToast(null)} variant="filled">
            {toast}
          </Alert>
        </Snackbar>

        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default VenueSupplierList;
